import { XIcon } from "@heroicons/react/outline";
import { ArrowLeftRightIcon, DownloadIcon, FileIcon } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { sizeConfig } from "@/components/ui/MediaBlocks/constants";
import { GenericFileBlockProps } from "@/components/ui/MediaBlocks/types";
import { downloadMedia } from "@/components/ui/MediaBlocks/utils";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

export const GenericFileBlock = ({
  media,
  onDelete,
  onReplace,
  readonly = true,
  size = "md",
}: GenericFileBlockProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const isXs = size === "xs";

  const tooltipContent = (
    <div className="flex flex-col gap-0.5">
      <span className="font-medium">{media.title || "File"}</span>
      <span className="truncate text-gray-300">{media.url}</span>
    </div>
  );

  if (isXs) {
    return (
      <BasicTooltip content={tooltipContent} side="top">
        <div
          className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-gray-100 transition-colors hover:bg-gray-200"
          onClick={() => downloadMedia(media.url, media.title || "file")}
        >
          <FileIcon className="h-8 w-8 text-gray-500" />
        </div>
      </BasicTooltip>
    );
  }

  return (
    <div
      className={cn(
        "group/media flex items-center justify-between rounded-lg border border-gray-200 bg-white",
        sizeConfig.padding[size],
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-lg bg-gray-100",
            sizeConfig.icon[size],
          )}
        >
          <FileIcon className={cn("text-gray-500", sizeConfig.iconInner[size])} />
        </div>
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-gray-900">{media.title || "File"}</span>
          <span className="truncate text-sm text-gray-500">{media.url}</span>
        </div>
      </div>
      <div className="ml-3 flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover/media:opacity-100">
        <Button
          type="button"
          variant="ghost"
          size="tinyIcon"
          onClick={() => downloadMedia(media.url, media.title || "file")}
          aria-label="Download file"
        >
          <DownloadIcon className="h-4 w-4" />
        </Button>
        {!readonly && onReplace && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="tinyIcon"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Replace file"
            >
              <ArrowLeftRightIcon className="h-4 w-4" />
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  onReplace(file);
                }
              }}
            />
          </>
        )}
        {!readonly && onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="tinyIcon"
            onClick={onDelete}
            aria-label="Delete file"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};
